import { groupBy, uniq } from "lodash-es";
import moment from "moment";
import { captureException } from "extensions/sentry";
import { toast } from "extensions/toast";
import sweetalert from "sweetalert";
import isWithinInterval from "date-fns/isWithinInterval";

import { dataAvailability } from "common/config/api";
import { store } from "common/store";
import { normalizeDateValueToUtcZ } from "common/utils";

import { request } from "common/components/request";

import { actions } from "common/reducers/dataAvailability";

// Current API statuses: held, error, processing, reprocessing, pending
const HELD = "held";
const REPROCESSING = "reprocessing";
const PROCESSING = "processing";
const PENDING = "pending";
const ERROR = "error";
const COMPLETE = "complete";
const COMPLETE_NO_DATA = "complete_no_data";
const COMPLETE_NO_REPORT = "complete_no_report";

// UI internal statuses
const WAITING = "waiting";
const MISSING = "missing_status"; // Used when there is no statuses for the date

const AUTHORIZATIONS = "authorizations";
const SETTLEMENTS = "settlements";
const CHARGEBACKS = "chargebacks";
const FEES = "fees";
const FRAUD = "fraud";
const UNKNOWN = "unknown";

const computeStatus = function(status = []) {
    if (status.includes(ERROR)) {
        return ERROR;
    }
    
    if (status.some((s) => [PROCESSING, REPROCESSING, HELD].includes(s))) {
        return PROCESSING;
    }
    
    if (status.includes(PENDING)) {
        return PENDING;
    }
    
    if (status.some((s) => [COMPLETE, COMPLETE_NO_DATA, COMPLETE_NO_REPORT].includes(s))) {
        return COMPLETE;
    }
    
    if (status.length === 0) {
        return MISSING;
    }
    
    return WAITING;
};

export const RESET_AVAILABILITY_STORE = () => store.dispatch(actions.RESET_AVAILABILITY_STORE());

export function FETCH_DATA_STATUS_IF_NEEDED({ datetype, dates }) {
    return async(dispatch, getState) => {
        const state = getState();
        
        await FETCH_ALL_REPORTS();
        
        if (SHOULD_FETCH_DATA_STATUS({ datetype, dates, state: state.dataAvailability })) {
            await FETCH_DATA_STATUS({ datetype, dates, dispatch });
        }
    };
}

/**
 * @return {boolean}
 */
export function SHOULD_FETCH_DATA_STATUS({
    state = store.getState().dataAvailability,
    datetype = "activity",
    dates
}) {
    if (state.requests[`${datetype}_${dates.start}_${dates.end}`] === 1) return false;
    
    if (state.dates[datetype]) {
        const startDate = moment.utc(dates.start);
        
        while (startDate.isBefore(dates.end)) {
            // If there is no date in the state then conclude that we should fetch it. We could change this function later
            // to check for partial available ranges, but re-request it for now
            if (!Object.prototype.hasOwnProperty.call(state.dates[datetype], startDate.clone().toISOString())) {
                return true;
            }
            
            startDate.add(1, "days");
        }
        
        return false;
    }
    
    return true;
}

export function FETCH_ALL_REPORTS() {
    const state = store.getState();
    
    if (state.dataAvailability.all_reports.length) {
        return Promise.resolve(state.dataAvailability.all_reports);
    }
    
    return request(dataAvailability.all_reports, {
        dataType: "json"
    }).then((data) => {
        const dataWithNormalizedDates = data.map((r) => ({
            ...r,
            start: r.start ? normalizeDateValueToUtcZ(r.start).toISOString() : r.start,
            end: r.end ? normalizeDateValueToUtcZ(r.end).toISOString() : r.end
        }));
        store.dispatch(actions.FETCH_ALL_REPORTS_SUCCESS({
            data: dataWithNormalizedDates
        }));
        return dataWithNormalizedDates;
    }, (error) => {
        // Just ignore the fact we didn't get anything from the API
        captureException(error);
        toast.error("There was an error fetching a list of all available reports");
        return store.dispatch(actions.FETCH_ALL_REPORTS_SUCCESS({ data: [] }));
    });
}

function GET_OK_REPORTS({ date, provider, exceptions = [] }) {
    const all_reports = store.getState().dataAvailability.all_reports;
    
    return all_reports.reduce((memo, report) => {
        // If it's for the different provider then return immediately.
        if (report.provider !== provider) return memo;
        // If the report has exception then return
        if (exceptions.some((reportWithException) => reportWithException.registered_fetcher_job_ref === report.registered_fetcher_job_ref)) return memo;
        
        const withinRange = typeof date === "undefined" || isWithinInterval(new Date(date), {
            start: new Date(report.start),
            end: new Date()
        });
        
        if (withinRange) {
            memo.push({
                name: report.name,
                type: report.type,
                start: report.start,
                end: report.end,
                registered_fetcher_job_ref: report.registered_fetcher_job_ref,
                status: COMPLETE
            });
        }
        return memo;
    }, []);
}

function GET_OK_REPORTS_BY_PROVIDER(date) {
    const all_reports = store.getState().dataAvailability.all_reports;
    
    const obj = all_reports.reduce((memo, report) => {
        // Check anomalies in reports API where some reports can have start date after the end date.
        const withinRange = report.start > report.end || isWithinInterval(date.toDate(), {
            start: new Date(report.start),
            end: report.end ? new Date(report.end) : new Date()
        });
    
        if (withinRange) {
            memo[report.provider] = memo[report.provider] || [];
            memo[report.provider].push({
                name: report.name,
                type: report.type,
                start: report.start,
                end: report.end,
                registered_fetcher_job_ref: report.registered_fetcher_job_ref,
                status: status.COMPLETE
            });
        }
        
        return memo;
    }, Object.create(null));
    
    return Object.entries(obj).map(([provider, reports]) => ({
        provider,
        reports
    }));
}

export function FETCH_DATA_STATUS({ datetype = "activity", dates, dispatch }) {
    return request(dataAvailability.status, {
        data: {
            datetype,
            start: dates.start,
            end: dates.end
        }
    }).then((data) => {
        // Javascript has a limitation on decimal places for milliseconds in UTC date.
        // Only three places is parsed and the rest is ignored
        // We should change all UTC keys to include only 3 digits for ms
        // Here we also inject OK reports
        const normalizedData = Object.entries(data).reduce((memo, [date, value]) => ({
            ...memo,
            [date.replace(".000000Z", ".000Z")]: value.map(({ provider, reports }) => {
                const allAvailableReports = groupBy(GET_OK_REPORTS({ provider }), "registered_fetcher_job_ref");
                
                return {
                    provider,
                    reports: [
                        ...reports.reduce((acc, cur) => {
                            if (cur.type === dataTypes.UNKNOWN) {
                                const knownReportsWithThisID = allAvailableReports[cur.registered_fetcher_job_ref];
                                if (knownReportsWithThisID?.length) {
                                    return [
                                        ...acc,
                                        ...knownReportsWithThisID.map(({ type }) => ({ // Override unknown report type with any known from all_reports
                                            ...cur,
                                            type
                                        }))
                                    ];
                                }
                            }
                            
                            return [
                                ...acc,
                                cur
                            ];
                        }, []),
                        ...GET_OK_REPORTS({ date, provider, exceptions: reports })
                    ]
                };
            })
        }), {});
        
        // Now we should add missing dates
        const start = moment.utc(dates.start);
        const end = moment.utc(dates.end);
        
        while (start < end) {
            let ok_reports = GET_OK_REPORTS_BY_PROVIDER(start);
            const iso_date = start.toISOString();
            
            if (Object.prototype.hasOwnProperty.call(normalizedData, iso_date)) {
                ok_reports = ok_reports.filter((reports) => !normalizedData[iso_date].some((r) => r.provider === reports.provider));
            }
    
            normalizedData[iso_date] = [
                ...normalizedData[iso_date] || [],
                ...ok_reports
            ];
            start.add(1, "day");
        }
        
        dispatch(actions.FETCH_DATA_AVAILABILITY_SUCCESS({
            data: normalizedData,
            datetype,
            dates
        }));
    });
}

// Actions that do not affect global store
export function GET_STATUS_FOR_DATE({
    datetype = "activity",
    date,
    state = store.getState().dataAvailability
}, {
    type = [],
    provider = [],
    report = [],
    subscription = []
} = {}) {
    if (!state.dates[datetype]) {
        throw new Error(`Unknown datetype: ${datetype}`);
    }
    
    const matchedDate = state.dates[datetype][date];
    if (matchedDate) {
        const status = matchedDate.reduce((memo, cur) => {
            if (provider.length === 0 || provider.includes(cur.provider)) {
                cur.reports.forEach((r) => {
                    if (conditionsChecker(r, { type, report, subscription })) {
                        memo.push(r.status);
                    }
                });
            }
            
            return memo;
        }, []);
        
        return computeStatus(status);
    }
    
    return MISSING;
}

export function GET_STATUS_FOR_DATERANGE({
    datetype,
    dates,
    state = store.getState().dataAvailability
}, {
    type = [],
    provider = [],
    report = [],
    subscription = []
} = {}) {
    const startDate = moment.utc(dates.start);
    const status = [];
    
    while (startDate.isBefore(dates.end)) {
        status.push(GET_STATUS_FOR_DATE({
            state,
            datetype,
            date: startDate.clone().toISOString()
        }, {
            type,
            provider,
            report,
            subscription
        }));
        startDate.add(1, "days");
    }
    
    return computeStatus(status);
}

export function GET_ALL_KNOWN_PROVIDERS({ datetype, state = store.getState().dataAvailability }) {
    if (!state.dates[datetype]) {
        throw new Error(`Unknown datetype: ${datetype}`);
    }
    
    const providers = Object.values(state.dates[datetype]).reduce((set, cur) => {
        cur.forEach(({ provider }) => set.add(provider));
        return set;
    }, new Set());
    
    // A short-hand way to convert a Set to an array via the ES6 spread operator.
    return [...providers].sort();
}

export function GET_ALL_KNOWN_TXN_TYPES() {
    return Object.values(dataTypes);
}

const conditionsChecker = function(report, conditions) {
    return (conditions.type.length === 0 || conditions.type.includes(report.type))
        && (conditions.report.length === 0 || conditions.report.includes(report.name))
        && (conditions.subscription.length === 0 || conditions.subscription.includes(report.reg_job_info));
};

export function GET_ALL_KNOWN_REPORTS({
    datetype,
    date,
    state = store.getState().dataAvailability,
    groupByRegJobRef = false
} = {}, {
    type = [],
    provider = [],
    report = [],
    subscription = []
} = {}) {
    if (!state.dates[datetype]) {
        throw new Error(`Unknown datetype: ${datetype}`);
    }
    
    const reportsObject = Object.values(state.dates[datetype]).reduce((obj, current) => {
        current.forEach((cur) => {
            if (provider.length === 0 || provider.includes(cur.provider)) {
                cur.reports.forEach((r) => {
                    if (conditionsChecker(r, { type, report, subscription })) {
                        if (groupByRegJobRef) {
                            obj[r.registered_fetcher_job_ref] = {
                                registered_fetcher_job_ref: r.registered_fetcher_job_ref,
                                name: r.name,
                                provider: cur.provider,
                                type: uniq([
                                    ...obj[r.registered_fetcher_job_ref]?.type || [],
                                    r.type
                                ])
                            };
                        } else {
                            const key = `${r.type}_${r.name}`;
                            obj[key] = {
                                registered_fetcher_job_ref: [...obj?.[key]?.registered_fetcher_job_ref || [], r.registered_fetcher_job_ref],
                                active_jobs: moment.utc().isBetween(r.start, r.end) ? [...obj?.[key]?.active_jobs || [], r.registered_fetcher_job_ref] : obj?.[key]?.active_jobs,
                                name: r.name,
                                type: r.type,
                                provider: cur.provider
                            };
                        }
                    }
                });
            }
        });
        
        return obj;
    }, {});
    
    return Object.values(reportsObject);
}

export function GET_ALL_KNOWN_SUBSCRIPTIONS({
    datetype,
    date,
    state = store.getState().dataAvailability
} = {}, {
    type = [],
    provider = [],
    report = [],
    subscription = []
} = {}) {
    if (!state.dates[datetype]) {
        throw new Error(`Unknown datetype: ${datetype}`);
    }
    
    const subscriptionsObject = Object.values(state.dates[datetype]).reduce((obj, current) => {
        current.forEach((cur) => {
            if (provider.length === 0 || provider.includes(cur.provider)) {
                cur.reports.forEach((r) => {
                    if (conditionsChecker(r, { type, report, subscription }) && r.reg_job_info) {
                        obj[`${r.reg_job_info}`] = r;
                    }
                });
            }
        });
        
        return obj;
    }, {});
    
    return Object.values(subscriptionsObject);
}

export function GET_REPORTS_FOR_DATE({
    datetype,
    date,
    state = store.getState().dataAvailability
} = {}, {
    type = [],
    provider = [],
    report = [],
    subscription = []
} = {}) {
    if (!state.dates[datetype]) {
        throw new Error(`Unknown datetype: ${datetype}`);
    }
    
    const matchedDate = state.dates[datetype][date];
    if (matchedDate) {
        return matchedDate.reduce((memo, cur) => {
            if (provider.length === 0 || provider.includes(cur.provider)) {
                cur.reports.forEach((r) => {
                    if (conditionsChecker(r, { type, report, subscription })) {
                        memo.push({
                            ...r,
                            provider: cur.provider
                        });
                    }
                });
            }
            
            return memo;
        }, []);
    }
    
    return [];
}

export function GET_REPORTS_FOR_DATERANGE({
    datetype,
    dates,
    state = store.getState().dataAvailability,
    groupByRegJobRef = false
} = {}, {
    provider = [],
    report = [],
    type = [],
    subscription = []
} = {}) {
    const startDate = moment.utc(dates.start);
    const reports = [];
    
    while (startDate.isBefore(dates.end)) {
        const reportsForDate = GET_REPORTS_FOR_DATE({
            state,
            datetype,
            date: startDate.clone().toISOString()
        }, {
            provider,
            report,
            type,
            subscription
        });
        
        reports.push(...reportsForDate);
        
        startDate.add(1, "days");
    }
    
    if (!groupByRegJobRef) return reports;
    
    // Group reports by their registered_fetcher_job_ref to prevent duplications
    const grouped = groupBy(reports, "registered_fetcher_job_ref");
    
    return Object.values(grouped).map((sameTypeReports) => ({
        ...sameTypeReports[0],
        type: sameTypeReports.map((r) => r.type)
    }));
}

export const status = {
    COMPLETE,
    PROCESSING,
    PENDING,
    ERROR,
    WAITING,
    HELD,
    REPROCESSING,
    COMPLETE_NO_DATA,
    COMPLETE_NO_REPORT
};

export const dataTypes = {
    AUTHORIZATIONS,
    SETTLEMENTS,
    CHARGEBACKS,
    FEES,
    FRAUD, // TODO MATT FRAUDSIGHT: Does Fraudsight need this?
    UNKNOWN
};

export const refetch = function(fetcher_job_ref) {
    return sweetalert({
        title: "Are you sure you want to refetch this job?",
        icon: "warning",
        buttons: {
            confirm: {
                text: "Refetch",
                className: "btn-confirm"
            },
            cancel: {
                visible: true
            }
        }
    }).then((confirmed) => {
        if (confirmed) {
            return request("/merchant/:merchant_ref/fetcher_jobs/refetch/", {
                method: "POST",
                data: {
                    fetcher_job_refs: [fetcher_job_ref]
                }
            }).then(() => {
                toast.success(`Fetcher job ${fetcher_job_ref} scheduled for refetch`);
            }).catch((error) => {
                toast.error(`There was an error scheduling ${fetcher_job_ref} for refetch`);
            });
        }
        
        return false;
    });
};
